import React, { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';

import { delayCardOptions } from 'constants/automation';
import { formatDelay } from 'modules/automation/utils/formatDelay';
import { calculateTotalDelay, getInheritedDelay, hasDelayAbove, hasOwnDelay } from 'modules/automation/utils/calculateTotalDelay';

export default function DelaySubCard({ index }) {
  const { control, setValue } = useFormContext();
  const actions = useWatch({ name: 'actions', control });
  const currentAction = actions?.[index];

  const inheritedDelay = useMemo(() => getInheritedDelay(actions, index), [actions, index]);
  const totalDelay = useMemo(() => calculateTotalDelay(actions, index), [actions, index]);
  const hasInheritedDelay = useMemo(() => hasDelayAbove(actions, index), [actions, index]);
  const hasCurrentOwnDelay = useMemo(() => hasOwnDelay(currentAction), [currentAction]);

  const clearDelay = () => {
    setValue(`actions.${index}.delay`, null, { shouldValidate: true });
  };

  const addDelay = () => {
    setValue(`actions.${index}.delay`, { value: 0, delayUnit: 'minutes', show: true }, { shouldValidate: true });
  };

  const showInheritedReadOnly = hasInheritedDelay && !hasCurrentOwnDelay;
  const showEditableDelay = hasCurrentOwnDelay;
  const showOnlyAddButton = !hasInheritedDelay && !hasCurrentOwnDelay;

  if (showOnlyAddButton) {
    return (
      <div className="time-delay-section mb-2">
        <div className="d-flex justify-content-between">
          <label htmlFor="timeDelay" className="form-label mb-0">
            <i className="fas fa-clock text-primary me-1"></i> Time Delay for this Action
          </label>
        </div>
        <Button
          icon="pi pi-plus"
          label="Add Time Delay"
          style={{ width: '150px', justifyContent: 'center', height: '35px' }}
          onClick={addDelay}
          className="p-button-primary p-button-sm mt-2"
        />
      </div>
    );
  }

  if (showEditableDelay) {
    return (
      <div className="time-delay-section mb-2">
        <div className="d-flex justify-content-between">
          <label htmlFor="timeDelay" className="form-label mb-0">
            <i className="fas fa-clock text-primary me-1"></i> Time Delay for this Action
          </label>
          <Button
            tooltip="Remove"
            tooltipOptions={{ showDelay: 500 }}
            icon="pi pi-times danger-text"
            className="p-button-rounded p-button-text text-danger"
            onClick={clearDelay}
          />
        </div>
        <div className="row g-2 align-items-center mb-2">
          <div className="col-auto">
            <Controller
              name={`actions.${index}.delay.value`}
              control={control}
              defaultValue={0}
              render={({ field }) => (
                <InputNumber
                  name={field.name}
                  onChange={(e) => field.onChange(e.value)}
                  value={field.value}
                  style={{ width: '100px' }}
                  min={0}
                />
              )}
            />
          </div>
          <div className="col-auto">
            <Controller
              name={`actions.${index}.delay.delayUnit`}
              defaultValue="minutes"
              control={control}
              render={({ field }) => (
                <Dropdown
                  name={field.name}
                  value={field.value}
                  options={delayCardOptions}
                  onChange={(e) => field.onChange(e.value)}
                  style={{ width: '120px' }}
                  inputRef={field.ref}
                />
              )}
            />
          </div>
        </div>
        {totalDelay > 0 ? (
          <div className="info-box text-primary mt-2">
            Based on your selections, this action will execute <strong>{formatDelay(totalDelay)}</strong> after the trigger.
          </div>
        ) : (
          <div className="info-box text-muted mt-2">Based on your selections, this action will execute immediately after the trigger.</div>
        )}
      </div>
    );
  }

  // If has inherited delay but no own delay - show info text + Add button
  if (showInheritedReadOnly) {
    return (
      <div className="time-delay-section mb-2">
        <div className="d-flex justify-content-between">
          <label htmlFor="timeDelay" className="form-label mb-0">
            <i className="fas fa-clock text-primary me-1"></i> Time Delay for this Action
          </label>
        </div>
        <Button
          icon="pi pi-plus"
          label="Add Time Delay"
          style={{ width: '150px', justifyContent: 'center', height: '35px' }}
          onClick={addDelay}
          className="p-button-primary p-button-sm mt-2"
        />
        <div className="info-box text-primary mt-2">
          Based on your selections, this action will execute <strong>{formatDelay(inheritedDelay)}</strong> after the trigger.
        </div>
      </div>
    );
  }

  return null;
}
